import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getSettings } from "../APIs/api-routes.util";
import { AppSettingsResponse } from "../APIs/api.types";
import { apiSubscribe } from "../lib/api-subscribe";
import { parseUrgentEnabled } from "../lib/app-settings.util";

interface AppSettingsContextValue {
  urgentEnabled: boolean;
  loading: boolean;
  refreshSettings: () => void;
  setUrgentEnabled: (enabled: boolean) => void;
}

const AppSettingsContext = createContext<AppSettingsContextValue>({
  urgentEnabled: true,
  loading: true,
  refreshSettings: () => {},
  setUrgentEnabled: () => {},
});

export function AppSettingsProvider({ children }: { children: ReactNode }) {
  const [urgentEnabled, setUrgentEnabledState] = useState(true);
  const [loading, setLoading] = useState(true);

  const refreshSettings = useCallback(() => {
    setLoading(true);
    return apiSubscribe<AppSettingsResponse>(getSettings(), {
      next: (body) => {
        setUrgentEnabledState(parseUrgentEnabled(body.enable_urgent_bookings));
        setLoading(false);
      },
      error: () => {
        setLoading(false);
      },
    });
  }, []);

  useEffect(() => refreshSettings(), [refreshSettings]);

  const setUrgentEnabled = useCallback((enabled: boolean) => {
    setUrgentEnabledState(enabled);
  }, []);

  const value = useMemo(
    () => ({
      urgentEnabled,
      loading,
      refreshSettings,
      setUrgentEnabled,
    }),
    [urgentEnabled, loading, refreshSettings, setUrgentEnabled]
  );

  return <AppSettingsContext.Provider value={value}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
  return useContext(AppSettingsContext);
}
